import _ from 'lodash';
import moment from 'moment';
import XLSX_STYLE from 'xlsx-js-style';
import ExportExcelTemplate from './ExportExcelTemplate';
import { fetchYearRawData, countCategoryTotals, formatCategoryItems } from './utils';

export function getPercent(amount, total) {
    return `${total ? ((amount / total) * 100).toFixed(2) : '0.00'}%`;
}

// 將API資料整理成 excel 的資料
export function formatDataRows(rawDatas, totals) {
    const LEFT_DATA = [
        {
            aheader: '資產',
            accno: '',
            accnam: '',
            ntTotalAmount: '金額',
            percent: '％',
        },
        {
            aheader: '',
            accno: '流動資產',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        ...rawDatas.currentAssets.map((o) => ({
            aheader: '',
            accno: '',
            accnam: o.accnam,
            ntTotalAmount: o.ntTotalAmount,
            percent: getPercent(o.ntTotalAmount, totals.assets),
        })),
        {
            aheader: '',
            accno: '流動資產總額',
            accnam: '',
            ntTotalAmount: totals.currentAssets,
            percent: getPercent(totals.currentAssets, totals.assets),
        },
        {
            aheader: '',
            accno: '',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        {
            aheader: '',
            accno: '非流動資產',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        ...rawDatas.nonCurrentAssets.map((o) => ({
            aheader: '',
            accno: '',
            accnam: o.accnam,
            ntTotalAmount: o.ntTotalAmount,
            percent: getPercent(o.ntTotalAmount, totals.assets),
        })),
        {
            aheader: '',
            accno: '非流動資產總額',
            accnam: '',
            ntTotalAmount: totals.nonCurrentAssets,
            percent: getPercent(totals.nonCurrentAssets, totals.assets),
        },
        {
            aheader: '',
            accno: '',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
    ];
    const LEFT_SUMMARY = [
        {
            aheader: '',
            accno: '資產總額',
            accnam: '',
            ntTotalAmount: totals.assets,
            percent: 100,
        },
    ];

    const RIGHT_LIABILITY_DATA = [
        {
            aheader: '負債',
            accno: '',
            accnam: '',
            ntTotalAmount: '金額',
            percent: '％',
        },
        {
            aheader: '',
            accno: '流動負債',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        ...rawDatas.currentLiabilities.map((o) => ({
            aheader: '',
            accno: '',
            accnam: o.accnam,
            ntTotalAmount: o.ntTotalAmount,
            percent: getPercent(o.ntTotalAmount, totals.liabilitiesAndEquity),
        })),
        {
            aheader: '',
            accno: '流動負債總額',
            accnam: '',
            ntTotalAmount: totals.currentLiabilities,
            percent: getPercent(totals.currentLiabilities, totals.liabilitiesAndEquity),
        },
        {
            aheader: '',
            accno: '',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        {
            aheader: '',
            accno: '非流動負債',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        ...rawDatas.nonCurrentLiabilities.map((o) => ({
            aheader: '',
            accno: '',
            accnam: o.accnam,
            ntTotalAmount: o.ntTotalAmount,
            percent: getPercent(o.ntTotalAmount, totals.liabilitiesAndEquity),
        })),
        {
            aheader: '',
            accno: '非流動資產總額',
            accnam: '',
            ntTotalAmount: totals.nonCurrentLiabilities,
            percent: getPercent(totals.nonCurrentLiabilities, totals.liabilitiesAndEquity),
        },
        {
            aheader: '',
            accno: '',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        {
            aheader: '',
            accno: '負債總額',
            accnam: '',
            ntTotalAmount: totals.liabilities,
            percent: getPercent(totals.liabilities, totals.liabilitiesAndEquity),
        },
    ];
    const RIGHT_TOTAL_DATA = [
        {
            aheader: '權益',
            accno: '',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        {
            aheader: '',
            accno: '股東權益',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
        ...rawDatas.totalEquity.map((o) => ({
            aheader: '',
            accno: '',
            accnam: o.accnam,
            ntTotalAmount: o.ntTotalAmount,
            percent: getPercent(o.ntTotalAmount, totals.liabilitiesAndEquity),
        })),
        {
            aheader: '',
            accno: '權益總額',
            accnam: '',
            ntTotalAmount: totals.totalEquity,
            percent: getPercent(totals.totalEquity, totals.liabilitiesAndEquity),
        },
        {
            aheader: '',
            accno: '',
            accnam: '',
            ntTotalAmount: '',
            percent: '',
        },
    ];

    const RIGHT_SUMMARY = [
        {
            aheader: '',
            accno: '負債與權益總計',
            accnam: '',
            ntTotalAmount: totals.liabilitiesAndEquity,
            percent: 100,
        },
    ];

    return {
        leftRows: LEFT_DATA,
        leftBottomRows: LEFT_SUMMARY,
        rightTopRows: RIGHT_LIABILITY_DATA,
        rightMidRows: RIGHT_TOTAL_DATA,
        rightBottomRows: RIGHT_SUMMARY,
    };
}

export function setupEmptyCell(sheet, maxRow) {
    const returnSheet = sheet;
    for (let i = 1; i <= maxRow; i += 1) {
        if (!sheet[`A${i}`]) returnSheet[`A${i}`] = { t: 's', v: '' };
        if (!sheet[`B${i}`]) returnSheet[`B${i}`] = { t: 's', v: '' };
        if (!sheet[`C${i}`]) returnSheet[`C${i}`] = { t: 's', v: '' };
        if (!sheet[`D${i}`]) returnSheet[`D${i}`] = { t: 's', v: '' };
        if (!sheet[`E${i}`]) returnSheet[`E${i}`] = { t: 's', v: '' };
        if (!sheet[`F${i}`]) returnSheet[`F${i}`] = { t: 's', v: '' };
        if (!sheet[`G${i}`]) returnSheet[`G${i}`] = { t: 's', v: '' };
        if (!sheet[`H${i}`]) returnSheet[`H${i}`] = { t: 's', v: '' };
        if (!sheet[`I${i}`]) returnSheet[`I${i}`] = { t: 's', v: '' };
        if (!sheet[`J${i}`]) returnSheet[`J${i}`] = { t: 's', v: '' };
    }
    return returnSheet;
}

// 建立 Excel 工作表
export function createExcelSheet(formatDatas, totals, companyName) {
    const {
        leftRows, leftBottomRows, rightTopRows, rightMidRows, rightBottomRows,
    } = formatDataRows(formatDatas, totals);
    let sheet = _.cloneDeep(ExportExcelTemplate);

    const LEFT_COL = 'A';
    const RIGHT_COL = 'F';
    const LEFT_TITLE_COL = 'B';
    const RIGHT_TITLE_COL = 'G';

    // 左側資料
    XLSX_STYLE.utils.sheet_add_json(sheet, leftRows, { skipHeader: true, origin: 'A2' });
    const END_OF_LEFT_FIRST_PART = +_.findKey(sheet, ['v', '流動資產總額']).split(LEFT_TITLE_COL)[1];

    // 右側資料
    XLSX_STYLE.utils.sheet_add_json(sheet, rightTopRows, { skipHeader: true, origin: `${RIGHT_COL}2` });

    // 判斷右側中間資料起始列後填入資料
    const END_OF_RIGHT_TOP_ROW = +_.chain(sheet)
        .filter((v, i) => i.includes(RIGHT_TITLE_COL))
        .findKey(['v', '負債總額'])
        .value();
    const START_OF_RIGHT_MID_PART = _.max([END_OF_LEFT_FIRST_PART + 1, END_OF_RIGHT_TOP_ROW + 4]);
    XLSX_STYLE.utils.sheet_add_json(sheet, rightMidRows, { skipHeader: true, origin: `${RIGHT_COL}${START_OF_RIGHT_MID_PART}` });

    // 判斷總結資料起始列後填入資料
    const END_OF_LEFT_SUMMARY_ROW = +_.chain(sheet)
        .filter((v, i) => i.includes(LEFT_TITLE_COL))
        .findKey(['v', '非流動資產總額'])
        .value() + 4;
    const END_OF_RIGHT_SUMMARY_ROW = +_.chain(sheet)
        .filter((v, i) => i.includes(RIGHT_TITLE_COL))
        .findKey(['v', '權益總額'])
        .value() + 4;
    const SUMMARY_ROW = _.max([END_OF_LEFT_SUMMARY_ROW, END_OF_RIGHT_SUMMARY_ROW]) + 1;
    XLSX_STYLE.utils.sheet_add_json(sheet, leftBottomRows, { skipHeader: true, origin: `${LEFT_COL}${SUMMARY_ROW}` });
    XLSX_STYLE.utils.sheet_add_json(sheet, rightBottomRows, { skipHeader: true, origin: `${RIGHT_COL}${SUMMARY_ROW}` });

    sheet = setupEmptyCell(sheet, SUMMARY_ROW);

    const widthSettings = [7, 15, 40, 15, 10, 7, 15, 40, 15, 10];
    sheet['!cols'] = _.map(widthSettings, (h) => ({ wch: h }));
    sheet['!rows'] = [{ hpx: 70 }];

    // let color;
    _.forEach(sheet, (value, key) => {
        if (!key.includes('!')) {
            const isLetterSingle = !Number.isNaN(Number(key.substring(1)));
            const COL = isLetterSingle ? key.substring(0, 1) : key.substring(0, 2);
            const ROW = isLetterSingle ? Number(key.substring(1)) : Number(key.substring(2));

            sheet[key].s = {};

            if (COL === 'A' || COL === 'B' || COL === 'G' || COL === RIGHT_COL) {
                sheet[key].s.font = {
                    bold: true,
                };
            }
            if (COL === 'E' || COL === 'J') {
                sheet[key].s.alignment = { horizontal: 'right' };
                if (!sheet[key].s.border) {
                    sheet[key].s.border = {
                        right: { style: 'thin', color: 'FFFFFFFF' },
                    };
                } else {
                    sheet[key].s.border.right = { style: 'thin', color: 'FFFFFFFF' };
                }
            }

            // 繪製隔線
            if (COL === 'A') {
                if (!sheet[key].s.border) sheet[key].s.border = { left: { style: 'thin', color: 'FFFFFFFF' } };
                else sheet[key].s.border.left = { style: 'thin', color: 'FFFFFFFF' };
            }
            if ((ROW === START_OF_RIGHT_MID_PART && ['F', 'G', 'H', 'I', 'J'].includes(COL)) || ROW === SUMMARY_ROW || ROW === 1 || ROW === 2) {
                if (!sheet[key].s.border) {
                    sheet[key].s.border = {
                        top: { style: 'thin', color: 'FFFFFFFF' },
                        bottom: { style: 'thin', color: 'FFFFFFFF' },
                    };
                } else {
                    sheet[key].s.border.top = { style: 'thin', color: 'FFFFFFFF' };
                    sheet[key].s.border.bottom = { style: 'thin', color: 'FFFFFFFF' };
                }
            }

            // 設定金額格式
            if (COL === 'D' || COL === 'I') {
                sheet[key].s.alignment = { horizontal: 'right' };
                sheet[key].z = '#,##0_-;-#,##0_-;0;General';
            }

            // 公司名稱標題
            if (key === 'A1') {
                sheet[key].v = `${companyName}\n資產負債表`;
                sheet[key].s.alignment = {
                    horizontal: 'center',
                    vertical: 'center',
                    wrapText: true,
                };

                sheet[key].s.font = {
                    bold: true,
                    sz: 16,
                };
            }
        }
    });

    return sheet;
}

// 下載 Excel
export async function downloadExcel(targetDate, companyName, directDownload = true) {
    const { startDate, queryDate, rawData } = await fetchYearRawData(targetDate);
    const formatDatas = formatCategoryItems(rawData, true);
    const totals = countCategoryTotals(formatDatas);

    const excelBook = XLSX_STYLE.utils.book_new();
    const excelSheet = createExcelSheet(formatDatas, totals, companyName);
    const fileName = `${moment(+startDate).format('YYYY-MM-DD')}～${moment(+queryDate).format('YYYY-MM-DD')}資產負債表.xlsx`;

    XLSX_STYLE.utils.book_append_sheet(excelBook, excelSheet, '資產負債表');
    if (directDownload) {
        return XLSX_STYLE.writeFile(excelBook, fileName);
    }
    const excelBuffer = XLSX_STYLE.write(excelBook, { type: 'array', bookType: 'xlsx' });
    return { buffer: excelBuffer, fileName };
}
